import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import apiClient from '../../../services/apiClient';

interface ScorecardData {
  summary: {
    total_trades: number;
    open_trades: number;
    wins: number;
    losses: number;
    win_rate: number | null;
    total_pnl: number;
    avg_pnl: number;
    avg_drawdown: number;
  };
  score_buckets: Record<string, { count: number; win_rate: number | null; avg_pnl: number | null }>;
  signal_combos: Array<{
    sentiment: string;
    gex_regime: string;
    near_call_wall: boolean;
    count: number;
    win_rate: number | null;
    avg_pnl: number | null;
  }>;
  wrong_calls: any[];
  suggestions: string[];
  open_positions: any[];
  closed_positions: any[];
}

const pnlColor = (v: number) => (v >= 0 ? 'success.main' : 'error.main');
const pnlStr = (v: number) => `${v >= 0 ? '+' : ''}$${v.toFixed(0)}`;
const wrColor = (wr: number | null) => {
  if (wr === null) return 'text.secondary';
  if (wr >= 60) return 'success.main';
  if (wr < 40) return 'error.main';
  return 'warning.main';
};

const PulseScorecard: React.FC = () => {
  const [data, setData] = useState<ScorecardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.get('/api/admin/pulse-scorecard')
      .then((r) => setData(r.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Auto-refresh every 60 seconds for live positions
  useEffect(() => {
    const interval = setInterval(() => {
      apiClient.get('/api/admin/pulse-scorecard')
        .then((r) => setData(r.data))
        .catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const s = data.summary;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>Pulse Scorecard</Typography>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        {[
          { label: 'Closed Trades', value: s.total_trades },
          { label: 'Open', value: s.open_trades },
          { label: 'Win Rate', value: s.win_rate != null ? `${s.win_rate}%` : '—', color: wrColor(s.win_rate) },
          { label: 'Total P&L', value: pnlStr(s.total_pnl), color: pnlColor(s.total_pnl) },
          { label: 'Avg P&L', value: pnlStr(s.avg_pnl), color: pnlColor(s.avg_pnl) },
          { label: 'Avg Drawdown', value: `$${s.avg_drawdown.toFixed(0)}` },
        ].map((card) => (
          <Paper key={card.label} variant="outlined" sx={{ p: 2, minWidth: 120, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">{card.label}</Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color: card.color || 'text.primary' }}>
              {card.value}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Open Positions */}
      {data.open_positions.length > 0 && (
        <>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
            Open Positions ({data.open_positions.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                  <TableCell>Ticker</TableCell>
                  <TableCell>Strategy</TableCell>
                  <TableCell align="right">Score</TableCell>
                  <TableCell>Sentiment</TableCell>
                  <TableCell align="right">Entry</TableCell>
                  <TableCell align="right">Live</TableCell>
                  <TableCell align="right">P&L</TableCell>
                  <TableCell align="right">Max Profit</TableCell>
                  <TableCell align="right">Drawdown</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell>Target/Stop</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.open_positions.map((t: any) => {
                  const pnl = parseFloat(t.live_pnl || '0');
                  return (
                    <TableRow key={t.id} hover>
                      <TableCell><Typography fontWeight={600}>{t.ticker}</Typography></TableCell>
                      <TableCell>{t.strategy_name?.replace(/_/g, ' ')}</TableCell>
                      <TableCell align="right">{t.score}</TableCell>
                      <TableCell>
                        <Chip label={t.sentiment} size="small"
                          color={t.sentiment === 'bullish' ? 'success' : t.sentiment === 'bearish' ? 'error' : 'default'} />
                      </TableCell>
                      <TableCell align="right">${parseFloat(t.entry_price || '0').toFixed(2)}</TableCell>
                      <TableCell align="right">{t.live_price ? `$${parseFloat(t.live_price).toFixed(2)}` : '—'}</TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 600, color: pnlColor(pnl) }}>{pnlStr(pnl)}</Typography>
                      </TableCell>
                      <TableCell align="right">{pnlStr(parseFloat(t.max_profit || '0'))}</TableCell>
                      <TableCell align="right">${parseFloat(t.max_drawdown || '0').toFixed(0)}</TableCell>
                      <TableCell>{t.expiry}</TableCell>
                      <TableCell>
                        {t.target ? `T:$${parseFloat(t.target).toFixed(0)}` : ''}
                        {t.target && t.stop ? ' / ' : ''}
                        {t.stop ? `S:$${parseFloat(t.stop).toFixed(0)}` : ''}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Score Buckets */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Accuracy by Score</Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700 } }}>
              <TableCell>Score Range</TableCell>
              <TableCell align="right">Trades</TableCell>
              <TableCell align="right">Win Rate</TableCell>
              <TableCell align="right">Avg P&L</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(data.score_buckets).map(([range, b]) => (
              <TableRow key={range}>
                <TableCell><Typography fontWeight={600}>{range}</Typography></TableCell>
                <TableCell align="right">{b.count}</TableCell>
                <TableCell align="right">
                  <Typography sx={{ color: wrColor(b.win_rate) }}>
                    {b.win_rate != null ? `${b.win_rate}%` : '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {b.avg_pnl != null ? <Typography sx={{ color: pnlColor(b.avg_pnl) }}>{pnlStr(b.avg_pnl)}</Typography> : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Signal Combinations */}
      {data.signal_combos.length > 0 && (
        <>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Accuracy by Signal Combination</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                  <TableCell>Sentiment</TableCell>
                  <TableCell>GEX Regime</TableCell>
                  <TableCell>Near Call Wall</TableCell>
                  <TableCell align="right">Trades</TableCell>
                  <TableCell align="right">Win Rate</TableCell>
                  <TableCell align="right">Avg P&L</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.signal_combos.map((c, i) => (
                  <TableRow key={i} sx={{ bgcolor: c.win_rate != null && c.win_rate < 30 ? 'error.main' : undefined, opacity: c.win_rate != null && c.win_rate < 30 ? 0.15 : 1 }}>
                    <TableCell>
                      <Chip label={c.sentiment} size="small"
                        color={c.sentiment === 'bullish' ? 'success' : c.sentiment === 'bearish' ? 'error' : 'default'} />
                    </TableCell>
                    <TableCell>{c.gex_regime}</TableCell>
                    <TableCell>{c.near_call_wall ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right">{c.count}</TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 600, color: wrColor(c.win_rate) }}>
                        {c.win_rate != null ? `${c.win_rate}%` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {c.avg_pnl != null ? <Typography sx={{ color: pnlColor(c.avg_pnl) }}>{pnlStr(c.avg_pnl)}</Typography> : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Suggestions */}
      {data.suggestions.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Suggested Improvements</Typography>
          {data.suggestions.map((s, i) => (
            <Typography key={i} variant="body2">- {s}</Typography>
          ))}
        </Alert>
      )}

      {/* Wrong Calls */}
      {data.wrong_calls.length > 0 && (
        <>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
            Wrong Calls ({data.wrong_calls.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                  <TableCell>Ticker</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Strategy</TableCell>
                  <TableCell align="right">Score</TableCell>
                  <TableCell>Sentiment</TableCell>
                  <TableCell>GEX</TableCell>
                  <TableCell>Near Wall</TableCell>
                  <TableCell align="right">P&L</TableCell>
                  <TableCell align="right">Drawdown</TableCell>
                  <TableCell>Factors</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.wrong_calls.map((t: any, i: number) => (
                  <TableRow key={i} hover>
                    <TableCell><Typography fontWeight={600}>{t.ticker}</Typography></TableCell>
                    <TableCell>{t.pulse_date}</TableCell>
                    <TableCell>{t.strategy_name?.replace(/_/g, ' ')}</TableCell>
                    <TableCell align="right">{t.score}</TableCell>
                    <TableCell>
                      <Chip label={t.sentiment} size="small"
                        color={t.sentiment === 'bullish' ? 'success' : t.sentiment === 'bearish' ? 'error' : 'default'} />
                    </TableCell>
                    <TableCell>{t.gex_regime}</TableCell>
                    <TableCell>{parseFloat(t.spot_vs_call_wall_pct || '-99') > -1 ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 600, color: 'error.main' }}>
                        {pnlStr(parseFloat(t.realized_pnl || '0'))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">${parseFloat(t.max_drawdown || '0').toFixed(0)}</TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ maxWidth: 200, display: 'block', whiteSpace: 'normal' }}>
                        {t.factors}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Recent Closed Trades */}
      {data.closed_positions.length > 0 && (
        <>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Recent Closed Trades</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                  <TableCell>Ticker</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Strategy</TableCell>
                  <TableCell align="right">Score</TableCell>
                  <TableCell>Sentiment</TableCell>
                  <TableCell align="right">Entry</TableCell>
                  <TableCell align="right">Exit</TableCell>
                  <TableCell align="right">P&L</TableCell>
                  <TableCell align="right">Max Drawdown</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.closed_positions.map((t: any, i: number) => {
                  const pnl = parseFloat(t.realized_pnl || '0');
                  return (
                    <TableRow key={i} hover>
                      <TableCell><Typography fontWeight={600}>{t.ticker}</Typography></TableCell>
                      <TableCell>{t.pulse_date}</TableCell>
                      <TableCell>{t.strategy_name?.replace(/_/g, ' ')}</TableCell>
                      <TableCell align="right">{t.score}</TableCell>
                      <TableCell>
                        <Chip label={t.sentiment} size="small"
                          color={t.sentiment === 'bullish' ? 'success' : t.sentiment === 'bearish' ? 'error' : 'default'} />
                      </TableCell>
                      <TableCell align="right">${parseFloat(t.entry_price || '0').toFixed(2)}</TableCell>
                      <TableCell align="right">{t.exit_price ? `$${parseFloat(t.exit_price).toFixed(2)}` : '—'}</TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 600, color: pnlColor(pnl) }}>{pnlStr(pnl)}</Typography>
                      </TableCell>
                      <TableCell align="right">${parseFloat(t.max_drawdown || '0').toFixed(0)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {s.total_trades === 0 && data.open_positions.length === 0 && (
        <Alert severity="info">
          No pulse paper trades yet. Trades will auto-open after the next pulse computation for tickers with score 7+.
        </Alert>
      )}
    </Box>
  );
};

export default PulseScorecard;
